// angular
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
// material
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
// 3rd party
import { v1 as generateUUID } from 'uuid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { mergeWith, Observable, Subject } from 'rxjs';


import { copyContentToClipboard } from '../../../../utils/miscellaneous';
import { takeUntil } from 'rxjs/operators';
import { FeedbackService } from '../../../../core/feedback.service';
import { getFullNameForFieldID } from '../../location-utils';
import { ColumnNote } from '../../../../models/locations-models/column-note';
import { getEnumValues } from '../../../../core/utilities';
import { Store } from '@ngrx/store';
import { NoteColor } from '../../enums/note-color';
import { memoize } from '../../../../core/decorators/memoize';
import { ColumnNotesActions } from '../state/location-list-actions';
import * as locationListSelectors from '../state/location-list-selectors';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { GxInputDirective } from '../../../../directives/gxinput.directive';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { NgClass, NgStyle } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';

@Component({
    selector: 'app-dynamic-column-note',
    templateUrl: './dynamic-column-note.component.html',
    styleUrls: ['./dynamic-column-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        MatIconButton,
        NgClass,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatInput,
        CdkTextareaAutosize,
        GxInputDirective,
        MatProgressSpinner,
        NgStyle,
        MatButtonToggleGroup,
        MatButtonToggle,
        TranslateModule,
    ],
})
export class DynamicColumnNoteComponent implements OnInit, OnDestroy {

  @Input() columnID: string;

  stickyNoteOpened = false;
  noteTextAreaFocused = false;

  colorChange = new Subject<ColumnNote>();
  textChange = new Subject<ColumnNote>();
  noteChange$ = this.textChange.pipe(
      mergeWith(this.colorChange));
  columnNote$: Observable<ColumnNote>;

  private componentDestroyed = new Subject<void>();

  // iterated in ngFor
  noteColors = getEnumValues(NoteColor);

  // cannot access textArea trough ViewChild if it's nested
  // within an *ngIf
  noteTextAreaID: string = generateUUID();
  getTextArea = () => document.getElementById(this.noteTextAreaID);

  // references
  @ViewChild(MatButtonToggleGroup, { static: true }) noteColor: MatButtonToggleGroup;
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;


  constructor(
      private translate: TranslateService,
      private feedbackService: FeedbackService,
      private store: Store,
  ) {
  }

  ngOnInit() {
    this.columnNote$ = this.store.select(locationListSelectors.selectColumnNote(this.columnID));

    this.noteChange$
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((columnNote) => {
          this.store.dispatch(ColumnNotesActions.updateColumnNote({ columnNote }));
        });
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * GETTERS & SETTERS
   */

  setMenuVisibility(visible: boolean) {
    this.stickyNoteOpened = visible;
  }


  // display max input length
  getLengthLimits(columnNote: ColumnNote) {
    const allowed = 4096;
    // for the textarea char counter
    const current = this.textareaValue?.length || 0;
    // for distinguishing bet. icons that change when the persisted state changes

    const currentPersisted = columnNote?.noteText?.length || 0;
    return { allowed, current, currentPersisted };
  }


  get textareaValue() {
    const textArea: HTMLElement = this.getTextArea();
    return textArea ? textArea['value'] : undefined;
  }

  /**
   * Returns a "pretty" name for a field ID
   */
  @memoize()
  getPrettyColumnName(columnId: string): string {
    return getFullNameForFieldID(columnId) + ' notes';
  }


  /**
   * FUNCTIONALITY
   */


  /**
   * Ensure the latest state from the datastore
   */
  onMenuOpen() {
    // save one more click and enable immediate typing
    this.getTextArea()?.focus();
  }

  /**
   * Adds the current date when applicable.
   * @param event
   */
  handleTextAreaDoubleClick(event: MouseEvent) {
    event.stopPropagation();

    const todayString = new Date().toISOString().slice(0, 10);
    if (!this.textareaValue.startsWith(todayString + ':\n')) {
      /*
        04.05.2021
        some text\n       <-- double \n if there already are previous values
        \n
        04.05.2021 \n     <-- single \n for the first time
        original text
      */
      this.getTextArea()['value'] = (
          todayString + ':\n' + (
              this.textareaValue
                  ? '\n\n' + this.textareaValue
                  : ''));

      // do mock an actual keyup event
      this.dispatchEmptyKeyup();
    }
  }

  /**
   * Dispatch change event
   * @param e
   * @param columnNote
   */
  onColorChange(e: MatButtonToggleChange, columnNote: ColumnNote) {
    const columnNoteCopy = { ...columnNote };
    columnNoteCopy.noteColor = e.value;
    columnNoteCopy.loading = true;
    // don't allow empty text? is this a design decision?
    if (columnNoteCopy.noteText?.trim()) {
      this.colorChange.next(columnNoteCopy);
    }
  }

  /**
   * Dispatch change event
   * @param e
   * @param columnNote
   */
  onTextChange(e: Event, columnNote: ColumnNote) {
    const columnNoteCopy = { ...columnNote };
    columnNoteCopy.noteText = e.target['value'];
    columnNoteCopy.loading = true;
    if (columnNoteCopy.noteText?.trim()) {
      this.textChange.next(columnNoteCopy);
    }
  }

  /**
   * Opens a popup asking the user if (s)he really wants to close it. If yes, sends BE
   * deletion request and closes the menu.
   */
  deleteColumnNote(e: MouseEvent, columnNote: ColumnNote) {
    e.stopPropagation();

    this.feedbackService.showConfirmationPopup({
      data: {
        title: this.translate.instant('ADDRESS_LIST.DYNAMIC_COLUMN_NOTES.MODAL.TITLE', { columnName: this.getPrettyColumnName(this.columnID) }),
        text: this.translate.instant('ADDRESS_LIST.DYNAMIC_COLUMN_NOTES.MODAL.TEXT', { columnName: this.getPrettyColumnName(this.columnID) }),
        submitButtonText: this.translate.instant('DELETE'),
        cancelButtonText: this.translate.instant('CANCEL'),
      },
    }).subscribe((val) => {
      if (val) {
        this.store.dispatch(ColumnNotesActions.deleteColumnNote({ columnNote: columnNote }));
      }
    });
  }

  /**
   * To make LEs' lives easier
   * @param $event the click event
   */
  copyNoteContent($event: MouseEvent) {
    $event.stopPropagation();

    copyContentToClipboard(this.getTextArea() as HTMLTextAreaElement, (ok) => {
      return ok && this.feedbackService.showSuccessSnackbar({
        data: { userFriendlyMessage: 'Copied!' },
        isAddressListSnackbar: true,
      });
    });
  }

  /**
   * Reusable dispatcher
   */
  dispatchEmptyKeyup() {
    this.getTextArea()?.dispatchEvent(new KeyboardEvent('input'));
  }

  @memoize()
  /**
   * Fixes BLS-4522.
   * Get column note text area value depending on its focus. Fakes an optimistic update in that we
   * only bind text area value to ngrx store (mirrors BE) when the col note's text area is not focused (edited).
   * Otherwise, user's input (the internal text area value) might get overwritten if a POST request
   * had been issued and the user continued to type sooner than the store could rehydrate its state
   * from BE response.
   * @param BeValue
   * @param internalValue text area value
   * @param noteTextAreaFocused
   */
  getNoteInputValue(BeValue, internalValue, noteTextAreaFocused): string {
    return BeValue && !noteTextAreaFocused ? BeValue : noteTextAreaFocused ? internalValue : '';
  }

}